import { useState, useCallback } from 'react';
import { trackAddToCart } from '@/lib/analytics';

export interface CartItem {
  id: string;
  productId?: string;
  name: string;
  price: number;
  oldPrice?: number;
  quantity: number;
  image: string;
  size?: string;
  categoryId?: string;
  categoryName?: string;
  subcategoryId?: string;
  subcategoryName?: string;
  components?: CartItemComponent[];
}

export interface CartItemComponent {
  id: string;
  title: string;
  quantity: number;
  comment?: string;
  componentType?: 'gift' | 'set_item';
}

export interface CartState {
  items: CartItem[];
  total: number;
  count: number;
}

const defaultCart: CartState = {
  items: [],
  total: 0,
  count: 0,
};

function getInitialCart(): CartState {
  try {
    const raw = localStorage.getItem('pchelovod-cart');
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  return defaultCart;
}

export function useCart() {
  const [cart, setCart] = useState<CartState>(getInitialCart);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>) => {
    trackAddToCart({
      product_id: item.productId ?? item.id,
      cart_item_id: item.id,
      name: item.name,
      price: item.price,
      old_price: item.oldPrice,
      size: item.size,
      category_id: item.categoryId,
      category_name: item.categoryName,
      subcategory_id: item.subcategoryId,
      subcategory_name: item.subcategoryName,
    });

    setCart((prev) => {
      const existing = prev.items.find((i) => i.id === item.id);
      let next: CartState;
      if (existing) {
        next = {
          items: prev.items.map((i) =>
            i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
          ),
          total: prev.total + item.price,
          count: prev.count + 1,
        };
      } else {
        next = {
          items: [...prev.items, { ...item, quantity: 1 }],
          total: prev.total + item.price,
          count: prev.count + 1,
        };
      }
      try { localStorage.setItem('pchelovod-cart', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setCart((prev) => {
      const item = prev.items.find((i) => i.id === id);
      if (!item) return prev;
      const next: CartState = {
        items: prev.items.filter((i) => i.id !== id),
        total: prev.total - item.price * item.quantity,
        count: prev.count - item.quantity,
      };
      try { localStorage.setItem('pchelovod-cart', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(id);
      return;
    }
    setCart((prev) => {
      const item = prev.items.find((i) => i.id === id);
      if (!item) return prev;
      const delta = quantity - item.quantity;
      const next: CartState = {
        items: prev.items.map((i) => (i.id === id ? { ...i, quantity } : i)),
        total: prev.total + item.price * delta,
        count: prev.count + delta,
      };
      try { localStorage.setItem('pchelovod-cart', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setCart(defaultCart);
    try { localStorage.removeItem('pchelovod-cart'); } catch { /* noop */ }
  }, []);

  return {
    cart,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
  };
}
